import React, { useState, useEffect } from 'react';
import './App.css';
import './components/css/BaobabDesignSystem.css';

// Import components
import { AuthProvider, useAuth } from './components/AuthContext';
import LoginForm from './components/LoginForm';
import ComprehensiveDocumentation from './components/ComprehensiveDocumentation';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Dashboard from './components/Dashboard';
import DocumentsManager from './components/DocumentsManager';
import ClientsCRM from './components/ClientsCRM';
import ContactsDirectory from './components/ContactsDirectory';
import UserManagement from './components/UserManagement';
import SettingsManager from './components/SettingsManager';
import OpportunitiesModule from './components/OpportunitiesModule';
import OperationsModule from './components/OperationsModule';
import AdvancedOperationsManager from './components/AdvancedOperationsManager';
import TransporteursModule from './components/TransporteursModule';
import ExportCenter from './components/ExportCenter';
import PipelineDashboard from './components/PipelineDashboard';
import CGTTemplatesManager from './components/CGTTemplatesManager';
import PaymentTermsManager from './components/PaymentTermsManager';

// Auth wrapper component
const AuthWrapper = ({ children }) => {
  const { loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="baobab-app min-h-screen flex items-center justify-center">
        <div className="baobab-loading">
          <div className="text-center">
            <div className="baobab-spinner mx-auto mb-4"></div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Chargement de l'application</h3>
            <p className="baobab-loading-text">Veuillez patienter...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginForm />;
  }

  return children;
};

// Main App component
const MainApp = () => {
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [operationCreateData, setOperationCreateData] = useState(null);
  const [showHelpCenter, setShowHelpCenter] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const { user } = useAuth();

  // Fonction de navigation depuis la recherche globale
  const handleNavigateFromSearch = (module, id) => {
    console.log('Navigation depuis recherche:', module, id);
    
    // Mapper les noms de modules de l'URL aux états de l'application
    const moduleMap = {
      'documents': 'documents',
      'opportunities': 'opportunities',
      'operations': 'operations',
      'clients': 'clients'
    };
    
    const targetMenu = moduleMap[module];
    if (targetMenu) {
      // Sauvegarder l'ID pour le module cible
      if (module === 'documents') {
        setSelectedDocumentId(id);
      }
      setActiveMenu(targetMenu);
    }
  };

  // Listen for navigation events from child components
  useEffect(() => {
    const handleNavigateToOperations = (event) => {
      const { create, opportunite_id, opportunite_nom, client } = event.detail;
      if (create) {
        setOperationCreateData({
          opportunite_id,
          opportunite_nom,
          client
        });
      }
      setActiveMenu('operations');
    };

    window.addEventListener('navigateToOperations', handleNavigateToOperations);
    return () => {
      window.removeEventListener('navigateToOperations', handleNavigateToOperations);
    };
  }, []);

  const renderContent = () => {
    switch (activeMenu) {
      case 'dashboard':
        return <Dashboard onNavigate={setActiveMenu} />;
      case 'conversion':
        return <Dashboard onNavigate={setActiveMenu} />;
      case 'opportunities':
        return <OpportunitiesModule />;
      case 'pipeline':
        return <PipelineDashboard />;
      case 'operations':
        return <OperationsModule 
          createData={operationCreateData} 
          onDataUsed={() => setOperationCreateData(null)}
        />;
      case 'advanced-operations':
        return <AdvancedOperationsManager />;
      case 'documents':
        return <DocumentsManager />;
      case 'cgt-templates':
        return (
          <div className="p-6">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Templates CGT</h2>
              <p className="text-gray-600">Gérez vos modèles de Conditions Générales de Transport</p>
            </div>
            <CGTTemplatesManager />
          </div>
        );
      case 'payment-terms':
        return (
          <div className="p-6">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Modalités de Paiement</h2>
              <p className="text-gray-600">Gérez vos conditions de paiement réutilisables</p>
            </div>
            <PaymentTermsManager />
          </div>
        );
      case 'transporteurs':
        return <TransporteursModule />;
      case 'contacts':
        return <ContactsDirectory />;
      case 'clients':
        return <ClientsCRM />;
      case 'export':
        return <ExportCenter />;
      case 'users':
        return <UserManagement />;
      case 'settings':
        return <SettingsManager />;
      default:
        return <Dashboard onNavigate={setActiveMenu} />;
    }
  };

  return (
    <div className="baobab-app">
      <div className="flex h-screen relative">
        {/* Sidebar */}
        <Sidebar 
          activeMenu={activeMenu} 
          setActiveMenu={setActiveMenu}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
        />
        
        {/* Mobile overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" 
            onClick={() => setSidebarOpen(false)}
          ></div>
        )}
        
        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden lg:ml-64">
          <Header 
            activeMenu={activeMenu} 
            user={user}
            onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
            onOpenHelpCenter={() => setShowHelpCenter(true)}
            onNavigateToModule={handleNavigateFromSearch}
          />
          
          <main className="flex-1 overflow-x-hidden overflow-y-auto">
            <div className="baobab-fade-in">
              {renderContent()}
            </div>
          </main>
        </div>
        
        {/* Centre d'aide */}
        {showHelpCenter && (
          <ComprehensiveDocumentation onClose={() => setShowHelpCenter(false)} />
        )}
      </div>
    </div>
  );
};

// App component with AuthProvider
const App = () => {
  return (
    <AuthProvider>
      <AuthWrapper>
        <MainApp />
      </AuthWrapper>
    </AuthProvider>
  );
};

export default App;